import { useSelector } from 'react-redux';
import { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { drawStadium } from './StadiumCanvas';
import computeShotXg from '../../utils/xgModel.js';
import { extractShotXg, resolvePlayerName, toFiniteNumber } from '../../utils/gameStatsHelpers';
import computePathClusters from '../../utils/pathClustering';

const toPoint = (value) => {
  if (!value) return null;
  if (Array.isArray(value) && value.length >= 2) {
    const x = toFiniteNumber(value[0]);
    const y = toFiniteNumber(value[1]);
    return x !== null && y !== null ? { x, y } : null;
  }
  const x = toFiniteNumber(value.x ?? value.X ?? value.left ?? value.lng);
  const y = toFiniteNumber(value.y ?? value.Y ?? value.top ?? value.lat);
  return x !== null && y !== null ? { x, y } : null;
};

const resolveShotOrigin = (shot) => {
  if (!shot || typeof shot !== 'object') return null;
  const candidates = [
    shot.from,
    shot.origin,
    shot.start,
    shot.source,
    shot.position,
    shot.pos,
    shot.location,
    shot.shot?.from,
    shot.shot?.origin,
    shot.result?.from,
    shot.result?.origin,
    shot.model?.shot?.from,
    shot.model?.shooter?.position,
    shot.metrics?.origin,
    shot.ball
  ];
  for (const candidate of candidates) {
    const point = toPoint(candidate);
    if (point) return point;
  }
  return null;
};

const namesMatch = (a, b) => {
  if (!a || !b) return false;
  if (a === b) return true;
  try {
    return a.toLowerCase() === b.toLowerCase();
  } catch (_) {
    return false;
  }
};

const computeCircleRadius = (xg) => {
  const bounded = Math.max(0, Math.min(1, Number(xg) || 0));
  const base = 3;
  const mid = Math.sqrt(bounded) * 10;
  const surge = Math.pow(bounded, 2.4) * 60;
  return base + mid + surge;
};

const inferShotTeam = (shot) => {
  const team = typeof shot?.team === 'string' ? shot.team.trim() : null;
  if (team === 'Red' || team === 'Blue') return team;
  const targetX = toFiniteNumber(shot?.to?.x);
  if (targetX !== null) {
    if (targetX > 0) return 'Red';
    if (targetX < 0) return 'Blue';
  }
  const origin = resolveShotOrigin(shot);
  if (origin && origin.x !== null) {
    if (origin.x > 0) return 'Red';
    if (origin.x < 0) return 'Blue';
  }
  return null;
};

const resolveShotGoalX = (shot, team) => {
  const goalX = toFiniteNumber(shot?.to?.x);
  if (goalX !== null) return goalX;
  if (team === 'Red') return 420;
  if (team === 'Blue') return -420;
  return 420;
};

const resolveShotXg = (shot, stadium) => {
  const direct = extractShotXg(shot);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const origin = resolveShotOrigin(shot);
  if (!origin) return 0.05;
  try {
    const team = inferShotTeam(shot);
    const toX = toFiniteNumber(shot?.to?.x);
    const toY = toFiniteNumber(shot?.to?.y);
    const goalX = resolveShotGoalX(shot, team);
    const goalHeight = 130;
    const goalCenterY = toY !== null ? toY : 0;
    const goal = {
      x: goalX,
      yTop: goalCenterY - goalHeight / 2,
      yBottom: goalCenterY + goalHeight / 2
    };
    const vx = toFiniteNumber(shot?.ball?.vx) ?? (toX !== null ? toX - origin.x : 0);
    const vy = toFiniteNumber(shot?.ball?.vy) ?? (toY !== null ? toY - origin.y : 0);
    const ball = {
      x: origin.x,
      y: origin.y,
      vx: Number.isFinite(vx) ? vx : 0,
      vy: Number.isFinite(vy) ? vy : 0
    };
    const isOnTarget = shot?.onTarget !== false;
    const result = computeShotXg({
      goal,
      ball,
      stadium: stadium || null,
      shooter: {
        position: { x: origin.x, y: origin.y },
        velocity: { x: ball.vx, y: ball.vy }
      },
      isOnTarget
    });
    const computed = Number(result?.xg);
    return Number.isFinite(computed) ? Math.max(0, computed) : 0.05;
  } catch (_) {
    return 0.05;
  }
};

function HeatMap() {

  const matches = useSelector(state => state.gameStats.matches)
  const mtc = useSelector(state => state.gameStats.selectedMatch)
  const playerPos = useSelector(state => state.gameStats.playerPos)
  const playerList = useSelector(state => state.gameStats.playerList)
  const selectedPlayer = useSelector(state => state.gameStats.selectedPlayer)
  const selectedHeatmap = useSelector(state => state.gameStats.selectedHeatmap)
  const canvasRef = useRef(null);

  const currentMatch = useMemo(() => {
    if (!Array.isArray(matches) || mtc == null) return null;
    return matches[mtc] || null;
  }, [matches, mtc]);

  const stadium = currentMatch?.stadium || null;
  const heatmapKey = selectedHeatmap || 'Heatmap';
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });

  const basePlayer = useMemo(() => {
    if (selectedPlayer && selectedPlayer !== -1) return selectedPlayer;
    if (!currentMatch) return null;

    if (Array.isArray(currentMatch.redTeam) && currentMatch.redTeam.length) {
      return currentMatch.redTeam[0];
    }
    if (Array.isArray(currentMatch.blueTeam) && currentMatch.blueTeam.length) {
      return currentMatch.blueTeam[0];
    }

    if (Array.isArray(currentMatch.player) && currentMatch.player.length) {
      return currentMatch.player[0]?.nick || null;
    }

    return null;
  }, [currentMatch, selectedPlayer]);

  const positionLookup = useMemo(() => {
    const map = new Map();
    if (!currentMatch) return map;

    const positionsByMatch = playerPos?.[mtc];
    if (!positionsByMatch) return map;

    const collectFromArray = (names) => {
      names.forEach((name, idx) => {
        if (!name) return;
        const entry = Array.isArray(positionsByMatch) ? positionsByMatch[idx] : undefined;
        if (Array.isArray(entry) && entry.length) {
          map.set(name, entry);
        }
      });
    };

    if (Array.isArray(playerList) && playerList.length) {
      collectFromArray(playerList);
    }

    if (Array.isArray(currentMatch.player)) {
      currentMatch.player.forEach((p, idx) => {
        const name = p?.nick;
        if (!name || map.has(name)) return;
        let entry;
        if (Array.isArray(positionsByMatch)) {
          entry = positionsByMatch[idx];
        } else if (positionsByMatch && typeof positionsByMatch === 'object') {
          entry = positionsByMatch[name] ?? positionsByMatch[idx];
        }
        if (Array.isArray(entry) && entry.length) {
          map.set(name, entry);
        }
      });
    }

    if (positionsByMatch && typeof positionsByMatch === 'object') {
      Object.entries(positionsByMatch).forEach(([name, value]) => {
        if (!name || map.has(name)) return;
        if (Array.isArray(value) && value.length) {
          map.set(name, value);
        }
      });
    }

    return map;
  }, [playerPos, playerList, mtc, currentMatch]);

  const resolvedPlayerName = useMemo(() => {
    if (basePlayer && positionLookup.has(basePlayer)) return basePlayer;
    const iterator = positionLookup.keys();
    const fallback = iterator.next()?.value;
    return fallback || basePlayer || null;
  }, [basePlayer, positionLookup]);

  const selectedPositions = useMemo(() => {
    if (!resolvedPlayerName) return null;
    const raw = positionLookup.get(resolvedPlayerName);
    return Array.isArray(raw) ? raw : null;
  }, [positionLookup, resolvedPlayerName]);

  const pathClusters = useMemo(() => {
    if (!selectedPositions || selectedPositions.length < 12) return null;
    const clusterCount = Math.max(1, Math.min(4, Math.floor(selectedPositions.length / 250)));
    try {
      return computePathClusters(selectedPositions, { k: clusterCount, maxIterations: 12 });
    } catch (error) {
      console.warn('[GPU] Heatmap path clustering fallback:', error?.message || error);
      return null;
    }
  }, [selectedPositions]);

  const handleMouseMove = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    setZoomOrigin({
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y))
    });
  }, []);

  const handleMouseEnter = useCallback(() => setIsZoomed(true), []);
  const handleMouseLeave = useCallback(() => setIsZoomed(false), []);

  const canvasStyle = useMemo(() => ({
    margin: '0 auto',
    display: 'block',
    maxWidth: '100%',
    height: '100%',
    transform: isZoomed ? 'scale(1.4)' : 'scale(1)',
    transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
    transition: 'transform 160ms ease-out, transform-origin 80ms ease-out',
    willChange: 'transform'
  }), [isZoomed, zoomOrigin.x, zoomOrigin.y]);

  function drawArrow(ctx, from, to) {
    if (!from || !to) return;
    const { x: fromX, y: fromY } = from;
    const { x: toX, y: toY } = to;
    if (![fromX, fromY, toX, toY].every((v) => typeof v === 'number' && Number.isFinite(v))) return;

    var headlen = 15; // length of head in pixels
    var dx = toX - fromX;
    var dy = toY - fromY;
    var angle = Math.atan2(dy, dx);
    ctx.beginPath();
    ctx.moveTo(fromX, fromY);
    ctx.lineTo(toX, toY);
    ctx.lineTo(toX - headlen * Math.cos(angle - Math.PI / 6), toY - headlen * Math.sin(angle - Math.PI / 6));
    ctx.moveTo(toX, toY);
    ctx.lineTo(toX - headlen * Math.cos(angle + Math.PI / 6), toY - headlen * Math.sin(angle + Math.PI / 6));
    ctx.stroke();
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!stadium) return;
    drawStadium(ctx, stadium);

    if (!resolvedPlayerName || !currentMatch) return;

    if (heatmapKey === 'Heatmap') {
      const positions = selectedPositions;
      if (!Array.isArray(positions) || positions.length === 0) return;
      ctx.fillStyle = 'rgba(255, 0, 0, 0.006)';
      positions.forEach((pos) => {
        if (!pos) return;
        const { x, y } = pos;
        if (typeof x !== 'number' || typeof y !== 'number') return;
        ctx.beginPath();
        ctx.arc(x, y, 15, 0, 2 * Math.PI);
        ctx.fill();
      });
      if (Array.isArray(pathClusters) && pathClusters.length) {
        ctx.save();
        ctx.fillStyle = 'rgba(0, 122, 255, 0.25)';
        ctx.strokeStyle = 'rgba(0, 82, 255, 0.7)';
        ctx.lineWidth = 3;
        pathClusters.forEach((cluster) => {
          if (!cluster) return;
          const { x, y } = cluster;
          if (!Number.isFinite(x) || !Number.isFinite(y)) return;
          ctx.beginPath();
          ctx.arc(x, y, 18, 0, 2 * Math.PI);
          ctx.fill();
          ctx.stroke();
        });
        ctx.restore();
      }
      return;
    }

    const stat = heatmapKey.split(' ')[0];

    if (stat === 'Goals') {
      for (const goal of currentMatch.goals || []) {
        if (!goal || goal.scorer !== resolvedPlayerName) continue;
        const shot = goal.shot;
        const ballCoord = goal.ballCoord;
        if (!shot || !ballCoord) continue;
        ctx.beginPath();
        ctx.strokeStyle = 'red';
        ctx.lineWidth = 5;
        ctx.moveTo(shot.x, shot.y);
        ctx.lineTo(ballCoord.x, ballCoord.y);
        ctx.stroke();
      }
    } else if (stat === 'Assists') {
      for (const goal of currentMatch.goals || []) {
        if (!goal || goal.assist?.player !== resolvedPlayerName) continue;
        const assistShot = goal.assist?.shot;
        const finalShot = goal.shot;
        const ballCoord = goal.ballCoord;
        if (!assistShot || !finalShot || !ballCoord) continue;
        ctx.beginPath();
        ctx.strokeStyle = 'red';
        ctx.lineWidth = 5;
        ctx.moveTo(assistShot.x, assistShot.y);
        ctx.lineTo(finalShot.x, finalShot.y);
        ctx.lineTo(ballCoord.x, ballCoord.y);
        ctx.stroke();
        ctx.beginPath();
        ctx.fillStyle = 'red';
        ctx.arc(finalShot.x, finalShot.y, 10, 0, 2 * Math.PI);
        ctx.fill();
      }
    } else if (stat === 'Kicks') {
      for (const kick of currentMatch.kicks || []) {
        if (!kick || kick.player !== resolvedPlayerName) continue;
        if (typeof kick.x !== 'number' || typeof kick.y !== 'number') continue;
        ctx.beginPath();
        ctx.fillStyle = 'red';
        ctx.arc(kick.x, kick.y, 5, 0, 2 * Math.PI);
        ctx.fill();
      }
    } else if (stat === 'Passes') {
      for (const pass of currentMatch.passes || []) {
        const passOwner = pass?.player || (typeof pass === 'string' ? pass : null);
        if (passOwner !== resolvedPlayerName) continue;
        const from = pass.lastShot;
        const to = pass.shot;
        if (!from || !to) continue;
        ctx.beginPath();
        ctx.strokeStyle = 'red';
        ctx.lineWidth = 5;
        drawArrow(ctx, from, to);
      }
    } else if (stat === 'xG') {
      const shots = Array.isArray(currentMatch.shots) ? currentMatch.shots : [];
      for (const shot of shots) {
        if (!shot || typeof shot !== 'object') continue;
        const shooter = resolvePlayerName(shot.player ?? shot.shooter ?? shot.author);
        if (!namesMatch(shooter, resolvedPlayerName)) continue;
        const origin = resolveShotOrigin(shot);
        if (!origin) continue;
        const rawXg = resolveShotXg(shot, stadium);
        const safeXg = Number.isFinite(rawXg) ? Math.max(0, rawXg) : 0;
        const radius = computeCircleRadius(safeXg);
        ctx.beginPath();
        ctx.fillStyle = 'rgba(255, 0, 0, 0.45)';
        ctx.strokeStyle = 'rgba(255, 0, 0, 0.9)';
        ctx.lineWidth = 4;
        ctx.arc(origin.x, origin.y, radius, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
      }
    } else if (stat === 'Shots') {
      for (const shot of currentMatch.shots || []) {
        if (!shot || shot.player !== resolvedPlayerName) continue;
        if (!shot.from || !shot.to) continue;
        const outcomeLabel = typeof shot.outcome === 'string' ? shot.outcome.toLowerCase() : '';
        const offTokens = ['offtarget', 'off-target', 'miss', 'wide', 'post', 'bar'];
        const isOffTarget = shot.onTarget === false || offTokens.some((token) => outcomeLabel.includes(token));
        ctx.beginPath();
        ctx.strokeStyle = isOffTarget ? 'yellow' : 'red';
        ctx.lineWidth = 5;
        drawArrow(ctx, shot.from, shot.to);
      }
    } else if (stat === 'Saves') {
      for (const sv of currentMatch.saves || []) {
        if (!sv || sv.player !== resolvedPlayerName) continue;
        const x = typeof sv.x === 'number' ? sv.x : null;
        const y = typeof sv.y === 'number' ? sv.y : null;
        if (x === null || y === null) continue;
        ctx.beginPath();
        ctx.fillStyle = 'gold';
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;
        ctx.arc(x, y, 7, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
      }
    }
  }, [stadium, resolvedPlayerName, currentMatch, heatmapKey, selectedPositions, pathClusters]);

  return (
    <div
      className="heatmap-interactive"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
    >
      <canvas
        ref={canvasRef}
        width={500}
        height={250}
        style={canvasStyle}
      />
    </div>
  );
}

export default HeatMap;
